// ─────────────────────────────────────────────────────────────────────────────
// agentVoice.ts — WHOSE VOICE WILL ANSWER.
//
// Owner ruling 2026-08-27: leave the Rexy fallback and SURFACE it honestly.
// "TRUFFLE — SPEAKING WITH REXY'S VOICE" is honest; silence is not.
//
// ── WHAT IS ACTUALLY TRUE, MEASURED ────────────────────────────────────────
// The mapping is DERIVED, not listed: `server/voice_daemon/daemon.py:2720` builds
// `_Binding(domain.upper(), agent.upper(), agent.lower())` — THE VOICE ID IS THE
// AGENT ID. What backs it is assets on disk (`/home/admin/Rexy/voices/*.pt`,
// hash-verified against VOICES.lock) and `voice_registry.FALLBACK_AGENT = "rexy"`.
// Live from B-TTS :8754 /health today: voices = ['arthur', 'rexy']. TWO. Fourteen
// declared agents have none — Truffle and Alpha included.
//
// ── THIS FILE HOLDS NO LIST, AND THAT IS THE POINT ─────────────────────────
// Hardcoding ['arthur','rexy'] would be the exact fault the derivation avoids —
// it was right this morning and would be wrong the day an asset lands. The set
// is READ. If it cannot be read the surface says the voice is UNKNOWN; it never
// guesses, and it never claims an agent's own voice it has not seen.
//
// A LIST, NOT A KEYED OBJECT, deliberately. `MAP[agent]` walks the prototype
// chain and "toString" comes back a truthy function — that has already reached a
// render in this codebase once. `Array.includes` cannot.
//
// PROVISIONAL — THE ONE LINE THAT MOVES WHEN THE ROUTE LANDS.
// No Flask route exposes the voice set today: `/api/voice/availability` returns
// {mode, local_service, local_healthy} and carries no voices. The set lives only
// on the TTS service's own :8754 /health, which a browser cannot reach. Reported
// to Sparticus rather than invented client-side.
// ─────────────────────────────────────────────────────────────────────────────

import { apiBase, authToken } from './apiBase';

export const AGENT_VOICE_PATH = '/voice/voices';

export interface AgentVoice {
  /** Have we actually read the voice set? False ⇒ say so, never guess. */
  known: boolean;
  /** The voice that will answer for this agent — its own, or the fallback. */
  voice: string | null;
  /** True when `voice` is NOT this agent's own. The placeholder, made visible. */
  isPlaceholder: boolean;
}

export const UNKNOWN_VOICE: AgentVoice = { known: false, voice: null, isPlaceholder: false };

export interface VoiceSet {
  /** Agent ids that have a real voice asset, lowercase. */
  voices: string[];
  /** The voice used for every agent without one. */
  fallback: string | null;
}

// apiBase and authToken are SHARED (src/api/apiBase.ts). This file used to
// carry its own copy of both, as five sibling clients did — six identical
// hardcodings of a host, and a built bundle pointed every one of them at
// herexy.com wherever it was served from. An instance fix leaves twins, so the
// answer moved to one module rather than being corrected here.


/** The wire body → the voice set. NEVER throws. An unreadable body is an EMPTY
 *  set with a null fallback, which resolves to `known: false` for every agent —
 *  not to "nobody has a voice", which would be a claim we cannot make. */
export function normalizeVoiceSet(d: any): VoiceSet {
  const voices = Array.isArray(d?.voices)
    ? d.voices.filter((v: any) => typeof v === 'string' && v.trim())
        .map((v: string) => v.trim().toLowerCase())
    : [];
  const fallback = typeof d?.fallback === 'string' && d.fallback.trim()
    ? d.fallback.trim().toLowerCase() : null;
  return { voices, fallback };
}

/**
 * Whose voice answers for `agent`. Pure.
 *
 * THE THREE HONEST ANSWERS, and none of them is a guess:
 *   known:false            we have not read the set. Say so.
 *   own voice              the agent has an asset. `isPlaceholder` false.
 *   the fallback           the agent has none. `isPlaceholder` TRUE, and the
 *                          surface names the voice that will actually speak.
 */
export function voiceForAgent(agent: string | null, set: VoiceSet | null): AgentVoice {
  const id = String(agent ?? '').trim().toLowerCase();
  if (!id || !set) return UNKNOWN_VOICE;
  if (set.voices.indexOf(id) >= 0) return { known: true, voice: id, isPlaceholder: false };
  if (set.fallback) return { known: true, voice: set.fallback, isPlaceholder: true };
  // An empty set is not evidence that this agent has no voice — it is evidence
  // we read nothing. Those are different facts and must not render alike. (An
  // earlier explicit guard for this was REMOVED: it duplicated this return
  // exactly, so no mutation could convict it — a branch a bar cannot reach is a
  // branch that is not being tested.)
  return UNKNOWN_VOICE;
}

/** GET the live voice set. Throws on non-2xx so the surface shows UNKNOWN. */
export async function getVoiceSet(): Promise<VoiceSet> {
  const res = await fetch(`${apiBase()}${AGENT_VOICE_PATH}`, {
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken()}` },
  });
  if (!res.ok) throw new Error(`agent_voice_http_${res.status}`);
  return normalizeVoiceSet(await res.json());
}
